import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Send, RefreshCw, Bot, User, Sparkles, ShoppingCart } from "lucide-react";
import { API } from "../constants";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import ChatMarkdown from "../components/ChatMarkdown";

const SESSION_KEY = "cap_chat_session_id";

const getOrCreateSessionId = () => {
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = `chat_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
};

const TypingDots = () => (
  <div className="flex items-center gap-1 px-4 py-3">
    <span className="w-2.5 h-2.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
    <span className="w-2.5 h-2.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
    <span className="w-2.5 h-2.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
  </div>
);

const ProductCard = ({ product }) => {
  const { addToCart } = useCart();
  return (
    <div className="flex items-center gap-3 bg-[#151515] border border-white/10 rounded-sm p-3 hover:border-[#D62828]/50 transition-all group" data-testid={`page-product-${product.product_id}`}>
      <div className="w-14 h-14 bg-[#1A1A1A] rounded-sm overflow-hidden flex-shrink-0">
        <img src={product.image_url || 'https://images.unsplash.com/photo-1692486407015-3a47e4c1181c'} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500 uppercase tracking-wide">{product.category}</p>
        <Link to={`/products/${product.product_id}`} className="block hover:text-[#D4AF37] transition-colors">
          <p className="text-white text-sm font-semibold line-clamp-1">{product.name}</p>
        </Link>
        <p className="text-[#D62828] font-bold">${product.online_price?.toFixed(2)}</p>
      </div>
      <Link
        to={`/products/${product.product_id}`}
        className="flex-shrink-0 px-3 py-2 border border-white/20 hover:border-[#D4AF37] rounded-sm text-white text-xs font-semibold transition-colors"
        data-testid={`page-view-${product.product_id}`}
      >View</Link>
      <button
        onClick={() => addToCart(product.product_id)}
        className="flex-shrink-0 px-3 py-2 bg-[#D62828] hover:bg-[#B91C1C] rounded-sm text-white text-xs font-semibold transition-colors flex items-center gap-1.5"
        data-testid={`page-add-to-cart-${product.product_id}`}
      >
        <ShoppingCart className="w-3.5 h-3.5" />Add
      </button>
    </div>
  );
};

const SUGGESTED_QUESTIONS = [
  "What's the best compound for deep scratches?",
  "How do I get a mirror finish on aluminum?",
  "Tell me about VIP membership benefits",
  "What buffing wheel should I start with?",
  "Do you ship to Canada?",
  "What's your return policy?",
  "How long does shipping take?",
  "Sisal vs flannel wheels — what's the difference?",
];

const ChatPage = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(getOrCreateSessionId);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const { user } = useAuth();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    axios.get(`${API}/chat/history/${sessionId}`)
      .then(res => {
        if (res.data.messages?.length > 0) {
          setMessages(res.data.messages.map(m => ({ ...m, suggested_products: [] })));
        } else {
          setMessages([{
            role: "assistant",
            content: "Welcome to Cut Above Polish Shop! I'm your AI assistant powered by Jimmy Dandridge's 35+ years of polishing expertise.\n\nI can help you with:\n• Product recommendations for your specific needs\n• Polishing techniques and tips\n• VIP membership questions\n• Shipping, returns, and order info\n\nWhat can I help you with today?",
            suggested_products: []
          }]);
        }
      })
      .catch(() => {
        setMessages([{ role: "assistant", content: "Welcome to Cut Above Polish Shop! How can I help you today?", suggested_products: [] }]);
      });
    inputRef.current?.focus();
  }, [sessionId]);

  useEffect(scrollToBottom, [messages]);

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setMessages(prev => [...prev, { role: "user", content: msg, suggested_products: [] }]);
    setInput("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat/message`, { session_id: sessionId, message: msg, user_id: user?.user_id || null });
      setMessages(prev => [...prev, {
        role: "assistant",
        content: res.data.response,
        suggested_products: res.data.suggested_products || []
      }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry, having trouble right now. Call 352-808-8981 or email sales@cutabovepolishshop.store.", suggested_products: [] }]);
    }
    setLoading(false);
  };

  const clearChat = async () => {
    await axios.delete(`${API}/chat/history/${sessionId}`).catch(() => {});
    sessionStorage.removeItem(SESSION_KEY);
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-24 flex flex-col">
      <div className="max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 flex-1 flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-14 h-14 bg-gradient-to-br from-[#D62828] to-[#8B0000] rounded-sm flex items-center justify-center shadow-lg">
                <Bot className="w-8 h-8 text-white" />
              </div>
              <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-500 rounded-full border-2 border-[#050505]"></span>
            </div>
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl tracking-wide">CUT ABOVE ASSISTANT</h1>
              <p className="text-gray-400 text-sm flex items-center gap-2">
                <Sparkles className="w-3 h-3 text-[#D4AF37]" />
                Powered by Claude AI · 35+ years of polishing expertise
              </p>
            </div>
          </div>
          <button onClick={clearChat} className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white border border-white/10 hover:border-white/30 rounded-sm text-sm transition-colors" data-testid="clear-chat-btn">
            <RefreshCw className="w-4 h-4" /> New Chat
          </button>
        </div>

        {/* Messages area */}
        <div className="flex-1 bg-[#0A0A0A] border border-white/5 rounded-sm flex flex-col" style={{ minHeight: '400px', maxHeight: 'calc(100vh - 340px)' }}>
          <div className="flex-1 overflow-y-auto p-6 space-y-6" data-testid="chat-messages">
            {messages.map((msg, idx) => (
              <div key={idx}>
                <div className={`flex gap-3 ${msg.role === "user" ? "flex-row-reverse" : "flex-row"}`} data-testid={`chat-msg-${idx}`}>
                  <div className={`w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 mt-1 ${msg.role === "user" ? "bg-white/10" : "bg-[#D62828]"}`}>
                    {msg.role === "user" ? <User className="w-5 h-5 text-gray-300" /> : <Bot className="w-5 h-5 text-white" />}
                  </div>
                  <div className={`max-w-[75%] flex flex-col gap-1 ${msg.role === "user" ? "items-end" : "items-start"}`}>
                    <div className={`px-4 py-3 rounded-sm text-sm leading-relaxed whitespace-pre-wrap ${msg.role === "user" ? "bg-[#D62828] text-white" : "bg-[#1A1A1A] text-gray-200 border border-white/5"}`}>
                      {msg.role === "user" ? msg.content : <ChatMarkdown text={msg.content} />}
                    </div>
                    <span className="text-gray-600 text-xs px-1">
                      {msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'just now'}
                    </span>
                  </div>
                </div>
                {/* Product recommendation cards */}
                {msg.role === "assistant" && msg.suggested_products?.length > 0 && (
                  <div className="ml-12 mt-3 space-y-2 max-w-[75%]" data-testid={`page-products-${idx}`}>
                    <p className="text-gray-500 text-xs uppercase tracking-wide">Recommended products</p>
                    {msg.suggested_products.map(p => <ProductCard key={p.product_id} product={p} />)}
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex gap-3 flex-row">
                <div className="w-9 h-9 bg-[#D62828] rounded-sm flex items-center justify-center flex-shrink-0">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div className="bg-[#1A1A1A] border border-white/5 rounded-sm"><TypingDots /></div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Suggested questions on fresh chat */}
          {messages.length <= 1 && !loading && (
            <div className="px-6 pb-4 border-t border-white/5 pt-4">
              <p className="text-gray-500 text-xs mb-3 uppercase tracking-wide">Try asking</p>
              <div className="flex flex-wrap gap-2">
                {SUGGESTED_QUESTIONS.map((q, i) => (
                  <button key={i} onClick={() => sendMessage(q)} className="text-xs px-3 py-1.5 bg-[#151515] border border-white/10 rounded-sm text-gray-300 hover:border-[#D62828] hover:text-white transition-colors" data-testid={`suggested-q-${i}`}>{q}</button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="border-t border-white/5 p-4 bg-[#0D0D0D] rounded-b-sm">
            <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="flex gap-3" data-testid="chat-form">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about products, polishing techniques, orders..."
                className="flex-1 bg-[#151515] border border-white/10 focus:border-[#D62828] text-white placeholder:text-gray-600 rounded-sm px-4 py-3 text-sm outline-none transition-colors"
                data-testid="chat-page-input"
                disabled={loading}
              />
              <button type="submit" disabled={loading || !input.trim()} className="px-4 py-3 bg-[#D62828] hover:bg-[#B91C1C] text-white rounded-sm flex items-center gap-2 disabled:opacity-40 transition-colors font-semibold text-sm" data-testid="chat-page-send-btn">
                <Send className="w-4 h-4" />
                <span className="hidden sm:block">Send</span>
              </button>
            </form>
            <p className="text-gray-600 text-xs mt-2 text-center">
              AI may make mistakes. For urgent help call <a href="tel:352-808-8981" className="text-[#D62828] hover:underline">352-808-8981</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
